import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { PERM_ROLE_ADMIN, PERM_ROLE_OWNER, userPermission } from '../../utils'
import { DOW_DATE_FORMAT, TIME_FORMAT } from '../../utils/dates'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps } from '../PocketComponent'
import { usePSComponent } from '../PocketComponent'
import Avatar from '../app/design/Avatar'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { HeaderButton, BaseRecordView } from '../common/sections'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (!urlParams.id) {
        throw new RecordNotFoundError('Event not found')
    }

    let event = await RecordFactory.getInstance().loadEvent(urlParams.id)

    if (!event) {
        throw new RecordNotFoundError('Event not found')
    }

    return { event }
}

const render = ({
    data,
    base: { me, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { event } = data

    const navigate = useNavigate()

    usePageTitle('Event')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
        secondaryButton?: {
            title: string
            onClick: () => void
        }
        isDestructive?: boolean
    } | null>(null)

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []
        const perm = me ? userPermission(me) : -1

        if (perm === PERM_ROLE_OWNER || perm === PERM_ROLE_ADMIN) {
            if (!!event.createdDate && !event.importedDate) {
                buttons.push({
                    title: 'Import appointment',
                    onClick: () =>
                        navigate(`/bookings/new?eventId=${encodeURIComponent(event.id ?? '')}`),
                })
                buttons.push({
                    title: 'Import & invoice',
                    onClick: () =>
                        navigate(`/invoices/new?memo=${encodeURIComponent(event.notes ?? '')}`),
                })
            }
        }

        if (!event.cancelledDate)
            buttons.push({
                title: 'Remove',
                intent: 'secondaryDestructive',
                onClick: () => {
                    setConfirmationModalData({
                        title: 'Remove this event',
                        body: 'Would you like to remove this event from your calendar?',
                        confirmationButtonText: 'Remove this event',
                        onConfirm: async () => {
                            setConfirmationModalData(null)
                            const result = await RecordFactory.getInstance().deleteEvent(event)
                            if (!result.ok) {
                                logAndDisplayError(result.error)
                            }
                            navigate('/calendar', { replace: true })
                        },
                        isDestructive: true,
                    })
                },
            })

        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^((\/events\/.*))/,
    })

    return (
        <BaseRecordView
            title="Appointment"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Events',
                          onClick: () => navigate('/calendar'),
                      }
            }
            headerButtons={headerButtons}
            tag={{ label: 'Sync', intent: 'default' }}
        >
            <BaseRecordView.Container>
                <BaseRecordView.LeftColumn>
                    <Card size="sm">
                        <div className="flex flex-col items-center justify-center gap-6">
                            <Avatar size="xxl">
                                <Icon icon="calendar.large" className="size-[56px]" />
                            </Avatar>

                            <div className="flex flex-col items-center justify-center gap-1">
                                <Heading size="sm" className="text-center">
                                    {event.title}
                                </Heading>
                                {!!event.date && (
                                    <Paragraph size="lg" className="font-normal text-gray-600">
                                        {event.date.format(DOW_DATE_FORMAT)}
                                    </Paragraph>
                                )}
                                {!!event.date && (
                                    <Paragraph size="lg" className="font-normal text-gray-600">
                                        {event.date.format(TIME_FORMAT)}
                                        {event.endDate && !event.endDate.isSame(event.date)
                                            ? ' - ' + event.endDate.format(TIME_FORMAT)
                                            : ''}
                                    </Paragraph>
                                )}
                            </div>
                        </div>
                    </Card>

                    {!!event.location && event.location.length > 0 && (
                        <BaseRecordView.LocationSection title="Location" location={event.location} />
                    )}
                </BaseRecordView.LeftColumn>

                <BaseRecordView.RightColumn>
                    {(event.url ?? '').trim().length > 0 && (
                        <Card title="Link">
                            <a
                                href={event.url}
                                className="text-primary-700 mt-6 block font-bold"
                                target="_blank"
                            >
                                <span className="underline">{event.url}</span>{' '}
                                <Icon icon="externalLink.medium" className="inline" />
                            </a>
                        </Card>
                    )}

                    {(event.memo ?? '').trim().length > 0 && (
                        <Card title="Memo">
                            <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                                {event.memo}
                            </Paragraph>
                        </Card>
                    )}
                </BaseRecordView.RightColumn>
            </BaseRecordView.Container>

            {confirmationModalData !== null && (
                <ConfirmationModal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    onConfirm={() => confirmationModalData.onConfirm()}
                    title={confirmationModalData.title}
                    cancelButtonText={confirmationModalData.secondaryButton?.title}
                    confirmButtonText={confirmationModalData.confirmationButtonText}
                    isDestructive={confirmationModalData.isDestructive}
                    shouldHideCancelButton={!confirmationModalData.secondaryButton}
                    onCancel={confirmationModalData.secondaryButton?.onClick}
                >
                    {confirmationModalData.body}
                </ConfirmationModal>
            )}
        </BaseRecordView>
    )
}

export const EventPage = usePSComponent(render, viewWillAppear)
